import {
  type CanvasComponent,
  Point,
  ViewportDescriptor,
  ViewportLimiter,
  ViewportLimitingMode,
  ViewportLimitingModeStringValues,
} from '@ardoq/yfiles';

const MINIMUM_VIEWPORT_SIZE = 500;
const PREVIEW_MINIMUM_VIEWPORT_SIZE = 176;

class ModernizedViewportLimiter extends ViewportLimiter {
  static Instance = new ModernizedViewportLimiter(MINIMUM_VIEWPORT_SIZE);
  static PreviewInstance = new ModernizedViewportLimiter(
    PREVIEW_MINIMUM_VIEWPORT_SIZE
  );
  private readonly minimumViewportSize: number;
  private constructor(minimumViewportSize: number) {
    super();
    this.minimumViewportSize = minimumViewportSize;
  }
  override limitViewport(
    canvasComponent: CanvasComponent,
    requestedViewport: ViewportDescriptor,
    _: ViewportLimitingMode | ViewportLimitingModeStringValues
  ): ViewportDescriptor {
    const {
      contentBounds,
      contentMargins,
      size: { width: canvasWidth, height: canvasHeight },
    } = canvasComponent;

    // Early return if canvas dimensions are invalid (initial render or hidden view).
    // width=0 → requestedWidth=0 → aspect=0/0=NaN → centerX=NaN → yFiles throws validation error
    if (
      !Number.isFinite(canvasWidth) ||
      !Number.isFinite(canvasHeight) ||
      canvasWidth <= 0 ||
      canvasHeight <= 0
    ) {
      return requestedViewport;
    }

    const {
      x: contentX,
      y: contentY,
      width: contentWidth,
      height: contentHeight,
    } = contentBounds.getEnlarged(contentMargins);

    const {
      center: { x: requestedCenterX, y: requestedCenterY },
      zoom: requestedZoom,
    } = requestedViewport;
    const requestedWidth = canvasWidth / requestedZoom;
    const requestedHeight = canvasHeight / requestedZoom;
    const requestedX = requestedCenterX - requestedWidth / 2;
    const requestedY = requestedCenterY - requestedHeight / 2;

    const aspect = requestedWidth / requestedHeight;

    let width = requestedWidth;
    let height = width / aspect;

    // #region viewport sizing
    if (aspect > 1) {
      // the viewport width is greater than its height.
      // apply a minimum viewport height:
      if (height < this.minimumViewportSize) {
        height = this.minimumViewportSize;
        width = height * aspect;
      }
      // apply a maximum viewport width:
      const maximumWidth = 2 * Math.max(contentWidth, contentHeight * aspect);
      width = Math.min(width, maximumWidth);
      height = width / aspect;
    } else {
      // the viewport height is greater than its width.
      // apply a minimum viewport width:
      if (width < this.minimumViewportSize) {
        width = this.minimumViewportSize;
        height = width / aspect;
      }

      // apply a maximum viewport height:
      const maximumHeight = 2 * Math.max(contentWidth / aspect, contentHeight);
      height = Math.min(height, maximumHeight);
      width = height * aspect;
    }
    // #endregion
    // #region viewport positioning
    // recalculate x and y, retaining the center point of the suggested viewport, and clamping to sensible bounds for panning.
    const suggestedCenterX = requestedX + requestedWidth / 2;
    const suggestedCenterY = requestedY + requestedHeight / 2;
    const minimumCenterX = contentX - 2 * width;
    const maximumCenterX = contentX + contentWidth + 2 * width;
    const minimumCenterY = contentY - 2 * height;
    const maximumCenterY = contentY + contentHeight + 2 * height;
    const centerX = Math.max(
      Math.min(suggestedCenterX, maximumCenterX),
      minimumCenterX
    );
    const centerY = Math.max(
      Math.min(suggestedCenterY, maximumCenterY),
      minimumCenterY
    );
    // #endregion

    const limitedZoom = canvasWidth / width;

    return new ViewportDescriptor(new Point(centerX, centerY), limitedZoom);
  }
}
export { ModernizedViewportLimiter };
